import type { Translation } from './en'

/**
 * Greek copy for the research lab.
 *
 * `Copy` keeps the English *shape* — every key, nested exactly as declared —
 * while widening the `as const` string literals back to plain `string`. So a
 * missing or misspelled key is a compile error, and an extra key is an
 * excess-property error, but a different Greek sentence is not. This is the same
 * mechanism apps/web-to-app/src/i18n/el.ts uses.
 *
 * ── How this is written ──
 *
 * Greek first, English second. Where a literal rendering would sound translated,
 * the Greek says the same thing the way a Greek speaker would say it — «κουβέντα»
 * for the everyday sense of conversation, «δεν μένουμε ποτέ μόνοι» rather than a
 * calque of "no privacy".
 *
 *  - Πληθυντικός ευγενείας throughout, without drifting into singular mid-screen.
 *  - Gender-neutral wherever Greek allows it: «Χωρίς σχέση» rather than
 *    «Ελεύθερος/η». A respondent should not have to pick a gender to answer a
 *    question about their relationship, and the slashes read as an afterthought.
 *  - Final -ν kept per the rule (μη/μην, δε/δεν, τη/την), not by habit.
 *  - «ό,τι» (whatever) and «ότι» (that) are distinguished.
 *  - `relationship_status` is deliberately NOT «οικογενειακή κατάσταση» — that is
 *    marital status on a government form, not what this question asks.
 *
 * The same guardrails apply as in en.ts: no personality types, no scores, no
 * compatibility, no clinical framing, no invented statistics. Consent copy is
 * reviewed text — translate it faithfully rather than idiomatically, and route
 * changes through the same review. Every figure under `interstitials` is
 * published research by other people and keeps its citation in `sources`.
 */
type Copy<T> = T extends string ? string : { -readonly [K in keyof T]: Copy<T[K]> }

export const el: Copy<Translation> = {
	brand: 'Bonded Research Lab',
	language: 'Γλώσσα',
	changeLanguage: 'Αλλαγή γλώσσας',
	back: 'Πίσω',
	continue: 'Συνέχεια',
	skip: 'Παράλειψη',
	loading: 'Φορτώνει…',
	retry: 'Δοκιμάστε ξανά',
	stepOf: 'Ερώτηση {{n}} από {{total}}',
	copy: 'Αντιγραφή',
	copied: 'Αντιγράφηκε',

	notOpen: {
		eyebrow: 'Δεν έχει ανοίξει',
		title: 'Αυτή η έρευνα δεν είναι ανοιχτή',
		body: 'Οι ερωτήσεις δεν έχουν οριστικοποιηθεί ακόμη. Δεν έχει συλλεχθεί τίποτα και δεν υπάρχει κάτι να συμπληρώσετε.',
	},

	/** Mirror of `home` in en.ts — the marketing landing page at `/`. */
	home: {
		eyebrow: 'Έρευνα Bonded',
		title: 'Τι κάνει μια κουβέντα να πάει βαθύτερα;',
		ledeResearch:
			'Μια σύντομη έρευνα για το πώς λειτουργεί στ’ αλήθεια η σύνδεση. Πέντε λεπτά, χωρίς λογαριασμό, χωρίς κανένα αποτέλεσμα για εσάς — και είναι σκόπιμο.',
		ledeApp:
			'Ρωτάμε επειδή φτιάχνουμε το Bonded, ένα παιχνίδι ερωτήσεων για δύο — για ζευγάρια, πρώτα ραντεβού και φίλους — και προτιμούμε να ξέρουμε τι βοηθάει, παρά να το μαντεύουμε.',
		ctaSurvey: 'Ξεκινήστε την έρευνα',
		// «Αποκτήστε», όχι «Κατεβάστε»: το κουμπί ανοίγει τη σελίδα bonded.studio/app,
		// όχι το App Store.
		ctaApp: 'Αποκτήστε την εφαρμογή',
		phoneAltHome: 'Η αρχική οθόνη της εφαρμογής Bonded σε iPhone, με το avatar σας και μια συνεδρία έτοιμη να ξεκινήσει',
		phoneAltProfile: 'Η οθόνη προφίλ στην εφαρμογή Bonded σε iPhone, με avatar, σύντομο βιογραφικό και απαντημένες ερωτήσεις',
		// Ονομαστική, όχι αιτιατική. Το `landing.terms` είναι «Όρους Χρήσης» επειδή
		// διαβάζεται μέσα στη φράση «αποδέχεστε τους …»· σκέτο σε υποσέλιδο θέλει
		// «Όροι Χρήσης».
		terms: 'Όροι Χρήσης',
		privacy: 'Πολιτική Απορρήτου',
	},

	landing: {
		eyebrow: 'Έρευνα Bonded',
		title: 'Τι κάνει μια κουβέντα να πάει βαθύτερα;',
		lede: '{{questions}} ερωτήσεις για το πώς λειτουργεί στ’ αλήθεια η σύνδεση για εσάς. Πέντε λεπτά, χωρίς λογαριασμό, χωρίς σωστές απαντήσεις.',
		points: {
			why: {
				icon: '🔬',
				title: 'Γιατί ρωτάμε',
				body: 'Φτιάχνουμε μια εφαρμογή για να μιλάτε με τους ανθρώπους που σας νοιάζουν. Προτιμούμε να ξέρουμε τι βοηθάει στ’ αλήθεια, παρά να το μαντεύουμε.',
			},
			what: {
				icon: '📊',
				title: 'Τι γίνονται οι απαντήσεις',
				body: 'Οι απαντήσεις σας γίνονται κομμάτι ενός συνόλου. Σκοπεύουμε να δημοσιεύσουμε όσα βρούμε.',
			},
			you: {
				icon: '🙈',
				title: 'Τι παίρνετε πίσω',
				body: 'Τίποτα προσωπικά για εσάς — καμία βαθμολογία, κανένας τύπος, κανένα αποτέλεσμα. Και είναι σκόπιμο.',
			},
		},
		cta: 'Ας ξεκινήσουμε',
		agreementPrefix: 'Συνεχίζοντας, αποδέχεστε τους',
		terms: 'Όρους Χρήσης',
		and: 'και την',
		privacy: 'Πολιτική Απορρήτου',
	},

	consent: {
		eyebrow: 'Πριν ξεκινήσετε',
		title: 'Μία απόφαση πρώτα',
		points: {
			purpose: {
				icon: '🔬',
				body: 'Οι απαντήσεις σας χρησιμοποιούνται για έρευνα γύρω από τη σύνδεση και τη συζήτηση, και για ευρήματα που ίσως δημοσιεύσουμε.',
			},
			aggregate: {
				icon: '👥',
				body: 'Τίποτα από όσα δημοσιεύουμε δεν περιγράφει ένα άτομο. Κάθε νούμερο που αντιπροσωπεύει λιγότερους από πέντε ανθρώπους αποκρύπτεται αυτόματα.',
			},
			noProfile: {
				icon: '🚫',
				body: 'Δεν θα βαθμολογηθείτε, δεν θα καταταχθείτε σε κάποιον τύπο και δεν θα αντιστοιχιστείτε με κανέναν. Η έρευνα δεν βγάζει κανένα αποτέλεσμα για εσάς.',
			},
			control: {
				icon: '✉️',
				body: 'Οι απαντήσεις κρατιούνται όσο τρέχει αυτή η έρευνα. Στο τέλος θα πάρετε έναν κωδικό αναφοράς — στείλτε μας τον με email όποτε θέλετε και τις διαγράφουμε.',
			},
			// Mirrors `advertising` in en.ts — see the reasoning there.
			advertising: {
				icon: '📊',
				body: 'Το «ναι» επιτρέπει επίσης να φορτώσει το tag μέτρησης του TikTok, ώστε να ξέρουμε ποιες διαφημίσεις φέρνουν κόσμο εδώ. Το «όχι» το κρατά απενεργοποιημένο.',
			},
		},
		accept: 'Ναι, χρησιμοποιήστε τις απαντήσεις μου',
		decline: 'Όχι, όχι αυτή τη φορά',
		declineNote: 'Η άρνηση τερματίζει την έρευνα εδώ και δεν σας κοστίζει τίποτα.',
	},

	declined: {
		eyebrow: 'Κατανοητό',
		title: 'Ευχαριστούμε πάντως',
		body: 'Δεν καταγράφηκε τίποτα πέρα από την απόφασή σας να μη συμμετάσχετε — την κρατάμε για να ξέρουμε πόσο συχνά συμβαίνει.',
		waitlistCta: 'Μπείτε στη λίστα αναμονής',
		done: 'Κλείσιμο',
	},

	sections: {
		about_you: {
			title: 'Λίγα για εσάς',
			body: 'Τρεις γρήγορες ερωτήσεις, ώστε τα ευρήματα να μπορούν να αναλυθούν ανά ομάδα.',
		},
		connection_habits: {
			title: 'Πώς συνδέεστε',
			body: 'Πώς πάει συνήθως η κουβέντα για εσάς αυτή την περίοδο.',
		},
		loneliness: {
			title: 'Αίσθηση σύνδεσης',
			body: 'Τέσσερις γρήγορες ερωτήσεις για το πόσο συνδεδεμένοι νιώθετε στην καθημερινότητά σας.',
		},
		felt_responsiveness: {
			title: 'Να σας ακούνε',
			body: 'Τρεις ερωτήσεις για το πώς νιώθετε όταν μιλάτε σε έναν δικό σας άνθρωπο.',
		},
		self_disclosure: {
			title: 'Να ανοίγεστε',
			body: 'Τρεις ερωτήσεις για το πόσο εύκολο ή δύσκολο είναι να λέτε αυτό που πραγματικά σκέφτεστε.',
		},
		closing: {
			title: 'Κάτι άλλο;',
			body: 'Μία προαιρετική ερώτηση, με δικά σας λόγια.',
		},
	},

	interstitials: {
		connection_habits: {
			title: 'Όσοι δηλώνουν μεγαλύτερη ευεξία περνούν μεγαλύτερο μέρος της ημέρας μιλώντας',
			caption: 'Ποσοστό των ξύπνιων ωρών σε κουβέντα, για τους πιο ευτυχισμένους και τους λιγότερο ευτυχισμένους συμμετέχοντες της μελέτης.',
			labels: { happiest: 'Πιο ευτυχισμένοι', unhappiest: 'Λιγότερο ευτυχισμένοι' },
		},
		loneliness: {
			title: 'Η μοναξιά είναι μεγαλύτερη στους νέους, όχι στους μεγαλύτερους',
			caption: 'Ποσοστό όσων λένε ότι νιώθουν πολύ ή αρκετά μόνοι. Η διαφορά είναι αντίστροφη από αυτό που υποθέτουν οι περισσότεροι.',
			labels: { age_19_29: '19–29 ετών', everyone: 'Όλοι', age_65_plus: '65 και άνω' },
		},
		felt_responsiveness: {
			title: 'Όλο και περισσότεροι Ευρωπαίοι λένε ότι δεν έχουν κανέναν να απευθυνθούν για βοήθεια',
			caption: 'Ποσοστό ενηλίκων στην ΕΕ που δηλώνουν ότι δεν έχουν κανέναν να απευθυνθούν για βοήθεια — ηθική, πρακτική ή οικονομική.',
			labels: { '2018': '2018', '2022': '2022' },
		},
		self_disclosure: {
			title: 'Οι πιο ευτυχισμένοι είχαν διπλάσιες ουσιαστικές κουβέντες',
			caption: 'Ποσοστό των καταγεγραμμένων συζητήσεων που ξέφευγαν από την επιφανειακή κουβέντα.',
			labels: { happiest: 'Πιο ευτυχισμένοι', unhappiest: 'Λιγότερο ευτυχισμένοι' },
		},
	},

	sources: {
		mehl: 'Mehl, Vazire, Holleran & Clark (2010), Psychological Science 21(4). 79 άτομα φορούσαν καταγραφείς ήχου επί τέσσερις ημέρες.',
		gallup: 'Meta-Gallup, Global State of Social Connections (2023). 142 χώρες, περίπου 1.000 άτομα στην καθεμία.',
		eurostat: 'Eurostat, ενότητα ποιότητας ζωής EU-SILC (2018 και 2022).',
	},

	questions: {
		age_band: {
			title: 'Σε ποια ηλικιακή ομάδα ανήκετε;',
			options: {
				'18_24': '18–24',
				'25_34': '25–34',
				'35_44': '35–44',
				'45_54': '45–54',
				'55_64': '55–64',
				'65_plus': '65 και άνω',
				prefer_not_to_say: 'Προτιμώ να μην πω',
			},
		},
		relationship_status: {
			title: 'Τι περιγράφει καλύτερα τη σχέση σας αυτή την περίοδο;',
			options: {
				single: 'Χωρίς σχέση',
				dating: 'Σε γνωριμία',
				partnered: 'Σε σχέση',
				married: 'Σε γάμο ή σύμφωνο συμβίωσης',
				separated: 'Μετά από χωρισμό ή διαζύγιο',
				widowed: 'Σε χηρεία',
				self_describe: 'Το περιγράφω αλλιώς',
				prefer_not_to_say: 'Προτιμώ να μην πω',
			},
		},
		living_situation: {
			title: 'Με ποιους μένετε αυτή την περίοδο;',
			help: 'Επιλέξτε όσα ισχύουν.',
			options: {
				alone: 'Μένω μόνος/η μου',
				partner: 'Με τον σύντροφό μου',
				children: 'Με παιδιά',
				family: 'Με γονείς ή άλλους συγγενείς',
				friends: 'Με φίλους ή συγκατοίκους',
				other: 'Με άλλη διευθέτηση',
				prefer_not_to_say: 'Προτιμώ να μην πω',
			},
		},
		confidant_count: {
			title: 'Σε πόσους ανθρώπους μπορείτε να μιλήσετε για κάτι προσωπικό ή σημαντικό;',
			options: {
				no_one: 'Σε κανέναν',
				one: 'Σε 1 άτομο',
				two_three: 'Σε 2–3 άτομα',
				four_six: 'Σε 4–6 άτομα',
				seven_plus: 'Σε 7 ή περισσότερα',
				prefer_not_to_say: 'Προτιμώ να μην πω',
			},
		},
		conversation_blockers: {
			title: 'Τι κάνει συνήθως δύσκολες τις πιο βαθιές κουβέντες για εσάς;',
			help: 'Επιλέξτε όσα ισχύουν — ή κανένα.',
			options: {
				dont_know_what_to_ask: 'Δεν ξέρω πώς να ξεκινήσω',
				feels_awkward: 'Νιώθω αμήχανα',
				fear_of_reaction: 'Φοβάμαι την αντίδραση',
				hard_to_find_moment: 'Δύσκολα βρίσκω την κατάλληλη στιγμή',
				stay_on_small_talk: 'Μένουμε στα επιφανειακά',
				no_right_person: 'Δεν έχω τον κατάλληλο άνθρωπο',
				nothing_stops_me: 'Τίποτα δεν με εμποδίζει',
				something_else: 'Κάτι άλλο',
			},
		},
		blocker_primary: {
			title: 'Ποιο από αυτά σας εμποδίζει περισσότερο;',
			help: 'Επιλέξτε αυτό που ταιριάζει καλύτερα.',
			options: {
				dont_know_what_to_ask: 'Δεν ξέρω πώς να ξεκινήσω',
				feels_awkward: 'Νιώθω αμήχανα',
				fear_of_reaction: 'Φοβάμαι την αντίδραση',
				hard_to_find_moment: 'Δύσκολα βρίσκω την κατάλληλη στιγμή',
				stay_on_small_talk: 'Μένουμε στα επιφανειακά',
				no_right_person: 'Δεν έχω τον κατάλληλο άνθρωπο',
				nothing_stops_me: 'Τίποτα δεν με εμποδίζει',
				something_else: 'Κάτι άλλο',
			},
		},
		blocker_other_text: {
			title: 'Τι άλλο σας εμποδίζει;',
			help: 'Λίγες λέξεις αρκούν — μπορείτε και να το παραλείψετε.',
			placeholder: 'Προαιρετικό',
			hint: 'Μη συμπεριλάβετε ονόματα, email ή τηλέφωνα — ούτε τα δικά σας.',
		},
		conversation_helpers: {
			title: 'Τι βοηθάει περισσότερο μια κουβέντα να είναι ουσιαστική για εσάς;',
			help: 'Επιλέξτε αυτό που ταιριάζει καλύτερα.',
			options: {
				feeling_listened_to: 'Να νιώθω ότι με ακούνε',
				honesty: 'Η ειλικρίνεια',
				right_questions: 'Οι σωστές ερωτήσεις',
				enough_time: 'Να υπάρχει αρκετός χρόνος',
				feeling_safe: 'Να νιώθω ασφάλεια, χωρίς κριτική',
				sharing_personal: 'Να μοιραστώ κάτι προσωπικό',
				something_else: 'Κάτι άλλο',
			},
		},
		dating_app_sentiment: {
			title: 'Τι θέση έχουν οι εφαρμογές γνωριμιών στη ζωή σας αυτή την περίοδο;',
			options: {
				never_used: 'Δεν έχω μπει ποτέ σε καμία',
				currently_using: 'Χρησιμοποιώ κάποια τώρα',
				burnt_out: 'Με έχουν κουράσει',
				quit: 'Τις παράτησα',
				prefer_not_to_say: 'Προτιμώ να μην πω',
			},
		},
		loneliness_companionship: {
			title: 'Πόσο συχνά νιώθετε ότι σας λείπει η συντροφικότητα;',
			low: 'Σχεδόν ποτέ',
			high: 'Συχνά',
			points: ['Σχεδόν ποτέ ή ποτέ', 'Κάποιες φορές', 'Συχνά'],
		},
		loneliness_left_out: {
			title: 'Πόσο συχνά νιώθετε ότι μένετε απ’ έξω;',
			low: 'Σχεδόν ποτέ',
			high: 'Συχνά',
			points: ['Σχεδόν ποτέ ή ποτέ', 'Κάποιες φορές', 'Συχνά'],
		},
		loneliness_isolated: {
			title: 'Πόσο συχνά νιώθετε απομονωμένοι από τους άλλους;',
			low: 'Σχεδόν ποτέ',
			high: 'Συχνά',
			points: ['Σχεδόν ποτέ ή ποτέ', 'Κάποιες φορές', 'Συχνά'],
		},
		loneliness_direct: {
			title: 'Πόσο συχνά νιώθετε μοναξιά;',
			low: 'Ποτέ',
			high: 'Συχνά ή πάντα',
			points: ['Ποτέ', 'Σχεδόν ποτέ', 'Πού και πού', 'Κάποιες φορές', 'Συχνά ή πάντα'],
		},
		responsiveness_understood: {
			title: 'Όταν μοιράζομαι κάτι προσωπικό με δικούς μου ανθρώπους, γενικά νιώθω ότι με καταλαβαίνουν.',
			low: 'Διαφωνώ απόλυτα',
			high: 'Συμφωνώ απόλυτα',
			points: ['Διαφωνώ απόλυτα', 'Διαφωνώ', 'Ούτε συμφωνώ ούτε διαφωνώ', 'Συμφωνώ', 'Συμφωνώ απόλυτα'],
		},
		responsiveness_cared_for: {
			title: 'Οι πιο δικοί μου άνθρωποι το καταλαβαίνουν όταν κάτι με απασχολεί, ακόμα κι αν δεν το πω.',
			low: 'Διαφωνώ απόλυτα',
			high: 'Συμφωνώ απόλυτα',
			points: ['Διαφωνώ απόλυτα', 'Διαφωνώ', 'Ούτε συμφωνώ ούτε διαφωνώ', 'Συμφωνώ', 'Συμφωνώ απόλυτα'],
		},
		responsiveness_attention: {
			title: 'Όταν μοιράζομαι κάτι προσωπικό, οι δικοί μου άνθρωποι μού δίνουν όλη τους την προσοχή.',
			low: 'Διαφωνώ απόλυτα',
			high: 'Συμφωνώ απόλυτα',
			points: ['Διαφωνώ απόλυτα', 'Διαφωνώ', 'Ούτε συμφωνώ ούτε διαφωνώ', 'Συμφωνώ', 'Συμφωνώ απόλυτα'],
		},
		disclosure_depth_ease: {
			title: 'Μου είναι εύκολο να μιλήσω για πράγματα που πραγματικά με αφορούν — φόβους, ελπίδες, αβεβαιότητες — με ανθρώπους που μόλις γνώρισα.',
			low: 'Πολύ δύσκολο',
			high: 'Πολύ εύκολο',
			points: ['Πολύ δύσκολο', 'Μάλλον δύσκολο', 'Ούτε εύκολο ούτε δύσκολο', 'Μάλλον εύκολο', 'Πολύ εύκολο'],
		},
		disclosure_gap: {
			title: 'Πόσο μεγάλη είναι η απόσταση ανάμεσα σε όσα συζητάτε εύκολα και σε όσα θα θέλατε να μπορείτε να συζητάτε;',
			options: {
				none: 'Καμία απόσταση',
				slight: 'Μικρή απόσταση',
				noticeable: 'Αισθητή απόσταση',
				large: 'Μεγάλη απόσταση',
			},
		},
		depth_satisfaction: {
			title: 'Πόσο ικανοποιημένοι είστε από το βάθος των κουβεντών στη ζωή σας;',
			low: 'Καθόλου ικανοποιημένος/η',
			high: 'Πολύ ικανοποιημένος/η',
			points: [
				'Καθόλου ικανοποιημένος/η',
				'Λίγο ικανοποιημένος/η',
				'Ούτε ικανοποιημένος/η ούτε δυσαρεστημένος/η',
				'Αρκετά ικανοποιημένος/η',
				'Πολύ ικανοποιημένος/η',
			],
		},
	},

	scale: { of: '{{value}} από {{total}}' },

	quote: {
		eyebrow: 'Προαιρετικό',
		title: 'Υπάρχει κάτι που θα θέλατε να καταλάβει κάποιος;',
		body: 'Μία-δυο προτάσεις, αν σας βγει. Μπορείτε να το παραλείψετε — οι περισσότεροι το κάνουν και δεν πειράζει.',
		label: 'Με δικά σας λόγια',
		placeholder: 'Προαιρετικό',
		hint: 'Μη συμπεριλάβετε ονόματα, email ή τηλέφωνα — ούτε τα δικά σας.',
		errors: {
			tooLong: 'Είναι λίγο μεγάλο — συντομεύστε το.',
			contact: 'Αφαιρέστε email, τηλέφωνα και ονόματα χρήστη.',
			failed: 'Δεν αποθηκεύτηκε. Δοκιμάστε ξανά ή παραλείψτε το.',
		},
		save: 'Συνέχεια',
		skip: 'Παραλείψτε το',
	},

	quoteConsent: {
		eyebrow: 'Μία τελευταία επιλογή',
		title: 'Μπορούμε να το παραθέσουμε;',
		body: 'Αν δημοσιεύσουμε ευρήματα, ίσως χρησιμοποιήσουμε αυτό που γράψατε — ποτέ με το όνομά σας και ποτέ δίπλα σε κάτι που σας ταυτοποιεί.',
		yours: 'Αυτό που γράψατε:',
		accept: 'Ναι, μπορείτε',
		decline: 'Όχι, κρατήστε το ιδιωτικό',
		declineNote: 'Η απάντησή σας μετράει στα ευρήματα έτσι κι αλλιώς.',
		remove: 'Διαγράψτε ό,τι έγραψα',
	},

	behaviorLink: {
		eyebrow: 'Επειδή έχετε λογαριασμό',
		title: 'Μπορούμε να τα συγκρίνουμε με το πώς χρησιμοποιείτε το Bonded;',
		body: 'Βλέποντας τις απαντήσεις σας δίπλα στο πώς εξελίσσονται οι συνεδρίες, θα μαθαίναμε αν η εφαρμογή κάνει αυτό που λένε οι άνθρωποι ότι θέλουν. Εντελώς προαιρετικό, και δεν αλλάζει τίποτα στον λογαριασμό σας.',
		note: 'Αφορά μόνο τη δική σας δραστηριότητα, ποτέ κάτι που έγραψε άλλο άτομο.',
		accept: 'Ναι, εντάξει',
		decline: 'Όχι, μόνο τις απαντήσεις μου',
	},

	email: {
		eyebrow: 'Τελευταίο βήμα',
		title: 'Πού να σας στείλουμε τα ευρήματα;',
		body: 'Αφήστε μια διεύθυνση και θα σας στείλουμε με email όσα δείξει αυτή η έρευνα. Αυτός είναι ο μόνος λόγος να αφήσετε μία.',
		emailLabel: 'Το email σας',
		placeholder: 'esy@paradeigma.gr',
		notice: 'Θα σας ειδοποιήσουμε επίσης όταν ανοίξει το Bonded. Μπορείτε να διαγραφείτε όποτε θέλετε.',
		onFileTitle: 'Θα χρησιμοποιήσουμε τη διεύθυνση του λογαριασμού σας',
		onFileBody: 'Δεν χρειάζεται να γράψετε κάτι — είστε συνδεδεμένοι, οπότε την έχουμε ήδη.',
		submit: 'Συνέχεια',
		position: 'Είστε ο αριθμός {{position}} στη λίστα.',
		joinedNoPosition: 'Είστε στη λίστα.',
		alreadyOnList: 'Ήσασταν ήδη στη λίστα.',
		errors: { email: 'Ελέγξτε τη διεύθυνση.', failed: 'Δεν ολοκληρώθηκε. Δοκιμάστε ξανά.' },
	},

	thanks: {
		eyebrow: 'Ολοκληρώθηκε',
		title: 'Ευχαριστούμε',
		body: 'Οι απαντήσεις σας καταγράφηκαν. Θα εμφανίζονται πάντα ως μέρος ενός συνόλου, ποτέ μόνες τους.',
		codeLabel: 'Ο κωδικός αναφοράς σας',
		codeHelp: 'Σας τον στείλαμε και με email. Κρατήστε τον, αν θελήσετε ποτέ να επικοινωνήσετε μαζί μας για τις απαντήσεις σας.',
		dataRequest: 'Διαχείριση των δεδομένων σας',
	},

	dataRequest: {
		eyebrow: 'Τα δεδομένα σας',
		title: 'Διαγραφή των απαντήσεών σας',
		body: 'Στείλτε email στο {{email}} αναφέροντας τον κωδικό σας. Θα διαγράψουμε τις απαντήσεις σας και θα σας το επιβεβαιώσουμε. Δεν χρειάζεται να δώσετε λόγο, ούτε να συμπληρώσετε κάτι άλλο.',
		codeLabel: 'Ο κωδικός αναφοράς σας',
		noCode: 'Αν δεν έχετε πια τον κωδικό σας, γράψτε μας από τη διεύθυνση που μας δώσατε και θα τον βρούμε.',
		back: 'Πίσω',
	},

	enter: {
		loading: 'Ανοίγει η έρευνα…',
		expiredTitle: 'Αυτός ο σύνδεσμος χρησιμοποιήθηκε',
		expiredBody:
			'Οι σύνδεσμοι της έρευνας ανοίγουν μία φορά και μετά λήγουν. Ανοίξτε ξανά την έρευνα από την εφαρμογή Bonded για νέο σύνδεσμο.',
	},

	errors: {
		title: 'Κάτι πήγε στραβά',
		body: 'Δικό μας θέμα. Οι απαντήσεις σας μέχρι τώρα είναι αποθηκευμένες.',
		offline: 'Φαίνεται ότι είστε εκτός σύνδεσης.',
	},
}
